import React, { useState } from 'react';
import {
  Check, X, User, Plus, Eye, Shield
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../../api/core.js';

// Liste de codes seulement : les libellés sont résolus au rendu par t().
const CONSENT_TYPES = ['telematics', 'performance_scoring', 'location_tracking', 'camera', 'biometric'];

function ConsentManagement({ onMessage }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [formData, setFormData] = useState({
    driver_id: '',
    consent_type: 'telematics',
    consented: true,
    legal_basis: 'consent',
    consent_version: '1.0',
    is_mandatory: false
  });

  const { data: driversData } = useQuery({
    queryKey: ['drivers'],
    queryFn: () => apiRequest('/driver')
  });

  // Fetch consent records (tenant-isolated via middleware)
  const { data: consentsData, isLoading } = useQuery({
    queryKey: ['consent_records'],
    queryFn: () => apiRequest('/gdpr-compliance/consent-records')
  });

  // Create consent record (org_id injected from JWT by middleware)
  const createMutation = useMutation({
    mutationFn: (data) => apiRequest('/gdpr-compliance/consent-records', {
      method: 'POST',
      body: JSON.stringify(data)
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['consent_records']);
      setShowForm(false);
      onMessage?.({ type: 'success', text: t('compliance.consent.toast.saved') });
    }
  });

  const drivers = driversData?.data || [];
  const consents = consentsData?.data || [];

  const getDriverConsents = (driverId) => consents.filter(c => c.driver_id === driverId);
  const hasConsent = (driverId, consentType) => {
    const record = consents.find(c => c.driver_id === driverId && c.consent_type === consentType);
    return record?.consented === 1;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  // Group consents by driver (reserved for future use)
  // const consentsByDriver = drivers.map(driver => ({
  //   driver,
  //   consents: getDriverConsents(driver.id)
  // }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t('compliance.consent.title')}</h3>
          <p className="text-sm text-gray-500">
            {t('compliance.consent.subtitle')}
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('compliance.consent.addButton')}
        </button>
      </div>

      {/* Consent Types Overview */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold mb-4">{t('compliance.consent.typesTitle')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CONSENT_TYPES.map(typeKey => {
            const count = consents.filter(c => c.consent_type === typeKey && c.consented === 1).length;
            return (
              <div key={typeKey} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">{t(`compliance.consent.types.${typeKey}.label`)}</span>
                  <span className="text-sm text-gray-500">{count}/{drivers.length}</span>
                </div>
                <p className="text-sm text-gray-600">{t(`compliance.consent.types.${typeKey}.description`)}</p>
                <div className="mt-2 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full"
                    style={{ width: `${(count / drivers.length) * 100 || 0}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Drivers Consent Matrix */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b">
          <h4 className="font-semibold">{t('compliance.consent.matrixTitle')}</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('compliance.consent.table.driver')}</th>
                {CONSENT_TYPES.map(typeKey => (
                  <th key={typeKey} className="px-4 py-3 text-center text-sm font-medium text-gray-700">
                    {t(`compliance.consent.types.${typeKey}.label`)}
                  </th>
                ))}
                <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">{t('compliance.consent.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={CONSENT_TYPES.length + 2} className="px-4 py-8 text-center text-gray-500">
                    {t('common.states.loading')}
                  </td>
                </tr>
              ) : drivers.length === 0 ? (
                <tr>
                  <td colSpan={CONSENT_TYPES.length + 2} className="px-4 py-8 text-center text-gray-500">
                    {t('compliance.consent.empty')}
                  </td>
                </tr>
              ) : (
                drivers.map(driver => (
                  <tr key={driver.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium">{driver.first_name} {driver.last_name}</div>
                          <div className="text-xs text-gray-500">{driver.email}</div>
                        </div>
                      </div>
                    </td>
                    {CONSENT_TYPES.map(typeKey => (
                      <td key={typeKey} className="px-4 py-3 text-center">
                        {hasConsent(driver.id, typeKey) ? (
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 text-green-600 rounded-full">
                            <Check className="w-4 h-4" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-red-100 text-red-600 rounded-full">
                            <X className="w-4 h-4" />
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => {
                          setSelectedDriver(driver);
                          setFormData({ ...formData, driver_id: driver.id });
                          setShowForm(true);
                        }}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legal Basis Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="font-semibold mb-4 flex items-center">
          <Shield className="w-5 h-5 mr-2 text-blue-600" />
          {t('compliance.consent.legalBasis.title')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <LegalBasisCard
            t={t}
            title={t('compliance.consent.legalBasis.consent.title')}
            article="Art. 6(1)(a)"
            description={t('compliance.consent.legalBasis.consent.description')}
            usage={t('compliance.consent.legalBasis.consent.usage')}
          />
          <LegalBasisCard
            t={t}
            title={t('compliance.consent.legalBasis.legitimateInterest.title')}
            article="Art. 6(1)(f)"
            description={t('compliance.consent.legalBasis.legitimateInterest.description')}
            usage={t('compliance.consent.legalBasis.legitimateInterest.usage')}
          />
          <LegalBasisCard
            t={t}
            title={t('compliance.consent.legalBasis.contract.title')}
            article="Art. 6(1)(b)"
            description={t('compliance.consent.legalBasis.contract.description')}
            usage={t('compliance.consent.legalBasis.contract.usage')}
          />
          <LegalBasisCard
            t={t}
            title={t('compliance.consent.legalBasis.legalObligation.title')}
            article="Art. 6(1)(c)"
            description={t('compliance.consent.legalBasis.legalObligation.description')}
            usage={t('compliance.consent.legalBasis.legalObligation.usage')}
          />
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {selectedDriver
                  ? t('compliance.consent.form.titleForDriver', { driver: `${selectedDriver.first_name} ${selectedDriver.last_name}` })
                  : t('compliance.consent.form.title')}
              </h3>
              <button onClick={() => { setShowForm(false); setSelectedDriver(null); }}>
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {!selectedDriver && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.consent.form.driver')}</label>
                  <select
                    value={formData.driver_id}
                    onChange={e => setFormData({ ...formData, driver_id: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">{t('common.actions.select')}</option>
                    {drivers.map(d => (
                      <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.consent.form.consentType')}</label>
                <select
                  value={formData.consent_type}
                  onChange={e => setFormData({ ...formData, consent_type: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {CONSENT_TYPES.map(typeKey => (
                    <option key={typeKey} value={typeKey}>{t(`compliance.consent.types.${typeKey}.label`)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('compliance.consent.form.legalBasis')}</label>
                <select
                  value={formData.legal_basis}
                  onChange={e => setFormData({ ...formData, legal_basis: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="consent">{t('compliance.consent.form.basis.consent')}</option>
                  <option value="legitimate_interest">{t('compliance.consent.form.basis.legitimateInterest')}</option>
                  <option value="contract">{t('compliance.consent.form.basis.contract')}</option>
                  <option value="legal_obligation">{t('compliance.consent.form.basis.legalObligation')}</option>
                </select>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <span className="font-medium">{t('compliance.consent.form.given')}</span>
                  <p className="text-sm text-gray-500">{t('compliance.consent.form.givenHint')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, consented: !formData.consented })}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    formData.consented ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    formData.consented ? 'left-7' : 'left-1'
                  }`} />
                </button>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_mandatory}
                  onChange={e => setFormData({ ...formData, is_mandatory: e.target.checked })}
                  className="mr-2"
                />
                <label className="text-sm text-gray-700">
                  {t('compliance.consent.form.mandatory')}
                </label>
              </div>

              <div className="border-t pt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setSelectedDriver(null); }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  {t('common.actions.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('common.actions.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function LegalBasisCard({ t, title, article, description, usage }) {
  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h5 className="font-medium">{title}</h5>
        <span className="text-xs bg-gray-100 px-2 py-1 rounded">{article}</span>
      </div>
      <p className="text-sm text-gray-600 mb-2">{description}</p>
      <p className="text-xs text-blue-600">
        <strong>{t('compliance.consent.legalBasis.usageLabel')}</strong> {usage}
      </p>
    </div>
  );
}

export default ConsentManagement;
